/// <amd-module name="DistributedTaskUI/SecurityComponent/ProjectPermissionsAuthorization/Components/ProjectAuthorizationComponent" />
import * as React from "react";
import { ProjectListComponent, IProjectListComponentProps } from "DistributedTaskUI/SecurityComponent/ProjectPermissionsAuthorization/Components/ProjectListComponent";
import * as Resources from "DistributedTaskUI/SecurityComponent/Resources";
import * as ProjectAuthorizationActionsCreator from "ProjectPermissionsAuthorization/Actions/ProjectAuthorizationActionsCreator";
import * as ProjectAuthorizationActionsHub from "ProjectPermissionsAuthorization/Actions/ProjectAuthorizationActionsHub";
import * as ProjectAuthorizationStore from "ProjectPermissionsAuthorization/Store/ProjectAuthorizationStore";
import * as ProjectAuthorizationSource from "ProjectPermissionsAuthorization/Source/ProjectAuthorizationSource";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { getTFSPageData } from "Tfs/Platform/Page";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Image } from "VSSUI/Image";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Table, ITableColumn, SimpleTableCell, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Link } from "VSSUI/Link";
import { Icon } from "VSSUI/Icon";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";

export interface IProjectAuthorizationSecurityProps {
    endpoint: ServiceEndpoint;
    canShareProjects: boolean;
    className?: string;
    updateServiceEndpointSharedFlag: (serviceEndpoint: ServiceEndpoint) => void;
    noProjectPermissionsViewLearnMoreLink?: string;
}

export class ProjectAuthorizationComponent extends VssComponent<IProjectAuthorizationSecurityProps, ProjectAuthorizationStore.IProjectPermissionsSecurityState>{
    constructor(props: IProjectAuthorizationSecurityProps, context: IVssComponentContext) {
        super(props, context);
        const tfsPageData = getTFSPageData(context.pageContext);
        let projectId = "";
        if (tfsPageData && tfsPageData.project) {
            projectId = tfsPageData.project.id;
        }
        this._actions = new ProjectAuthorizationActionsHub.ProjectAuthorizationActionsHub();
        this._source = new ProjectAuthorizationSource.ProjectAuthorizationSource(context.pageContext, projectId);
        this._actionsCreator = new ProjectAuthorizationActionsCreator.ProjectAuthorizationActionsCreator(this._actions, this.props.endpoint.id, this._source, this.props.updateServiceEndpointSharedFlag);
        this._actionsCreator.initialize();
        this._store = new ProjectAuthorizationStore.ProjectAuthorizationStore(this._actions, this.props.endpoint, context, projectId);
        this.state = this._store.getState();
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChange);
        this._store.dispose();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChange);
    }

    public render(): React.ReactNode {
        let projectListComponentProps: IProjectListComponentProps = {
            item: this.state.allProjects,
            endpoint: this.state.endpoint,
            actionsCreator: this._actionsCreator,
            canShareProjects: this.props.canShareProjects,
        };
        let gridContainerClassName = "grid-container";
        return (
            <div className={this.props.className + " flex flex-column"}>
                {this.state.error && this._getErrorCardComponent()}
                <Card className="bolt-card-no-vertical-padding flex-column project-permissions-card"
                    contentProps={{ contentPadding: false, className: "project-permissions-card-content" }}>
                    <h2 className="project-permissions-card-header">
                        {Resources.ProjectPermissionsTitle}
                    </h2>
                    <div className="project-permissions-card-header-button-group">
                        <ProjectListComponent {...projectListComponentProps} />
                    </div>
                    <div className="project-permissions-card-description secondary-text">
                        {Resources.ProjectPermissionsDescription}
                    </div>
                    <div className={gridContainerClassName}>
                        {this.state.projectReferences && this.state.projectReferences.length > 0 ?
                            this._getProjectPermissionsTable() : this._getNoProjectPermissionsView()}
                    </div>
                </Card>
            </div>
        );
    }

    private _getProjectPermissionsTable(): React.ReactNode {
        let itemsProvider = new ArrayItemProvider<ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow>(this.state.projectReferences);
        return (
            <Table ariaLabel={Resources.ProjectPermissionsTableName} className="roles-grid" scrollable={true} columns={this._tableColumns} itemProvider={itemsProvider} pageSize={20} role="presentation"/>
        );
    }

    private _getNoProjectPermissionsView(): React.ReactNode {
        Utils_Accessibility.announce(Resources.NoProjectPermissionsCardTitle);
        return (
            <div className="no-project-permissions-view justify-center">
                <Icon iconName={"Info"} className={"no-restrictions-icon"} ariaLabel="" />
                <div className="message-container">
                    <span className="message-header title-s">{Resources.NoProjectPermissionsCardTitle}</span>
                    <span>{Resources.NoProjectPermissionsCardContent}<br /> <Link ariaLabel={Resources.LearnMoreDescriptionForProjectPermissions} href={this.props.noProjectPermissionsViewLearnMoreLink}>{Resources.LearnMoreText}</Link></span>
                </div>
            </div>
        );
    }

    private _getErrorCardComponent(): JSX.Element {
        Utils_Accessibility.announce(this.state.error);
        return (
            <div>
                <MessageCard className="error-message-card" onDismiss={() => { this._actionsCreator.setErrorMessage(""); }} severity={MessageCardSeverity.Error}>
                    {this.state.error}
                </MessageCard>
            </div>
        );
    }

    private _onStoreChange = () => {
        let state = this._store.getState();
        this.setState(state);
    }

    private _tableColumns: ITableColumn<ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow>[] = [
        {
            id: "project",
            width: -80,
            name: Resources.ProjectButtonText,
            className: "project-button-cell",
            renderCell: this._renderProjectCellContent.bind(this)
        },
        {
            id: "removeButton",
            width: -20,
            className: "cross-button-cell",
            renderCell: this._renderRemoveCellContent.bind(this)
        }
    ];

    private _renderProjectCellContent(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow>, dataItem: ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow) {
        let projectReferenceName: string;
        this.state.endpoint.serviceEndpointProjectReferences.forEach(projectReference => {
            if (projectReference.projectReference.id === dataItem.projectId) {
                projectReferenceName = projectReference.name;
            }
        });

        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                key={"col-" + columnIndex}
                tableColumn={tableColumn}
                iconProps={{
                    render: (className: string): JSX.Element => this.renderProjectImage(dataItem)
                }}
                line1={
                    <Tooltip overflowOnly={true} text={dataItem.projectName}>
                        <span className={"body-m text-ellipsis"}>{dataItem.projectName}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true} text={projectReferenceName}>
                        <span className={"secondary-text body-s text-ellipsis"}>
                            <FPSLink className={"service-endpoint-link definition-link bolt-table-link bolt-link"} supportsFPS={false} href={dataItem.serviceEndpointLink}>{projectReferenceName}</FPSLink>
                        </span>
                    </Tooltip>
                }
            />
        );
    }

    private _renderRemoveCellContent(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow>, dataItem: ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow) {
        let removePermission = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            this._onRemove(dataItem);
        };
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Button tooltipProps={{ text: Resources.RevokeAccessTooltip }} primary={false} className="bolt-table-cell-content-reveal project-removal-button" subtle={true} iconProps={{ iconName: "Cancel", className: "removal-icon" }} ariaLabel={Resources.Removepermission} onClick={removePermission} />
            </SimpleTableCell>
        );
    }

    private _onRemove(dataItem: ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow): void {
        let projectIds: string[] = [dataItem.projectId];
        let projectNames: string[] = [dataItem.projectName];
        this._actionsCreator.deleteProjectReference(this.state.endpoint, projectIds, projectNames);
    }

    private renderProjectImage(dataItem: ProjectAuthorizationStore.IProjectPermissionsSecurityGridRow): JSX.Element {
        return <Image alt="project-pic" className="project-pic" src={dataItem.projectImageIconUrl} />;
    }

    private _actionsCreator: ProjectAuthorizationActionsCreator.ProjectAuthorizationActionsCreator;
    private _store: ProjectAuthorizationStore.ProjectAuthorizationStore;
    private _actions: ProjectAuthorizationActionsHub.ProjectAuthorizationActionsHub;
    private _source: ProjectAuthorizationSource.ProjectAuthorizationSource;
}
